import React from "react";
import {GridApi} from "@mui/x-data-grid-pro";
import {DynamicFormFieldDefinition} from "qqq/components/forms/DynamicFormUtils";
import {MIN_EDIT_COLUMN_WIDTHS, DEFAULT_MIN_EDIT_COLUMN_WIDTH} from "./rowEditConstants";

/***************************************************************************
 * Bump column widths up to the min-width appropriate for each editable
 * field's input type. Grow-only: never shrinks a column. Idempotent —
 * calling twice is safe.
 *
 * When columnVisibilityModel is provided, fields the model marks as hidden
 * (model[fieldName] === false) are skipped.  Reason: MUI X's setColumnWidth
 * goes through updateColumns which regenerates the visibility model from the
 * new colDef.  Because this app controls visibility via the model rather than
 * the colDef hide flag, the regen flips hidden columns visible — which fires
 * onColumnVisibilityModelChange and (in RecordQuery) cancels the active edit.
 * Skipping hidden columns avoids triggering that path; the user hid them on
 * purpose and they don't render an editor cell anyway.
 ***************************************************************************/
export function bumpColumnWidthsForEdit(
   apiRef: React.MutableRefObject<GridApi>,
   formFieldDefs: { [fieldName: string]: DynamicFormFieldDefinition },
   columnVisibilityModel?: { [field: string]: boolean }
): void
{
   for (const fieldName in formFieldDefs)
   {
      const def = formFieldDefs[fieldName];
      if (!def.isEditable)
      {
         continue;
      }

      if (columnVisibilityModel && columnVisibilityModel[fieldName] === false)
      {
         continue;
      }

      const lookupKey = def.possibleValueProps ? "possibleValue" : (def.type?.toLowerCase() ?? "");
      const minWidth = MIN_EDIT_COLUMN_WIDTHS[lookupKey] ?? DEFAULT_MIN_EDIT_COLUMN_WIDTH;

      const column = apiRef.current.getColumn(fieldName);
      if (!column)
      {
         continue;
      }
      const currentWidth = column.width ?? column.computedWidth ?? 0;
      if (currentWidth < minWidth)
      {
         apiRef.current.setColumnWidth(fieldName, minWidth);
      }
   }
}


/***************************************************************************
 * Capture the grid's current scroll position and return a function that
 * restores it.  Used to preserve scroll across transitions (e.g. toggling
 * disableVirtualization when exiting edit mode) that would otherwise cause
 * DataGridPro to re-layout and snap scroll back to (0, 0).
 ***************************************************************************/
/***************************************************************************
 * Capture the identity + viewport-relative horizontal position of the first
 * column currently visible in the grid's unpinned viewport.  Returns a
 * function that, when invoked after column widths have changed, adjusts
 * scrollLeft so that same column sits at the same visible x-offset again.
 *
 * Measures viewport positions via getBoundingClientRect and applies a
 * delta to scrollLeft — avoids offsetParent / column-virtualization
 * mismatches that skew pure offsetLeft-based calculations.
 ***************************************************************************/
export function preserveFirstVisibleColumn(apiRef: React.MutableRefObject<GridApi>): () => void
{
   const noop = () =>
   {
      ///////////////////////////////////////////////////////////////////////
      // no first-visible column found or grid missing, nothing to restore //
      ///////////////////////////////////////////////////////////////////////
   };
   const root = apiRef.current?.rootElementRef?.current as HTMLElement | undefined;
   const scroller = root?.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null;
   if (!root || !scroller)
   {
      return noop;
   }

   const scrollerRect = scroller.getBoundingClientRect();
   /////////////////////////////////////////////////////////////////////////////////
   // Find the first unpinned column header whose right edge is past the scroller //
   // viewport's left edge.  Pinned columns sit outside the scrollable area so we //
   // skip them — their position doesn't shift when unpinned widths change.       //
   /////////////////////////////////////////////////////////////////////////////////
   const headers = Array.from(root.querySelectorAll(".MuiDataGrid-columnHeader[data-field]")) as HTMLElement[];
   let firstVisible: HTMLElement | null = null;
   for (const header of headers)
   {
      if (header.closest(".MuiDataGrid-pinnedColumnHeaders"))
      {
         continue;
      }
      const rect = header.getBoundingClientRect();
      if (rect.right > scrollerRect.left)
      {
         firstVisible = header;
         break;
      }
   }
   if (!firstVisible)
   {
      return noop;
   }
   const field = firstVisible.getAttribute("data-field");
   if (!field)
   {
      return noop;
   }
   //////////////////////////////////////////////////////////////////////////////////
   // Distance from scroller's left edge to the header's left edge.  Usually       //
   // negative or zero (header's left is at or to the left of the viewport left).  //
   //////////////////////////////////////////////////////////////////////////////////
   const targetOffsetFromScrollerLeft = firstVisible.getBoundingClientRect().left - scrollerRect.left;

   /////////////////////////////////////////////////////////////////////////////////
   // Intended usage: call the returned fn INSIDE a flushSync wrapper around the  //
   // width-changing operation, immediately after.  flushSync forces React to     //
   // commit the new widths synchronously; this fn then measures and snaps scroll //
   // in the same synchronous block so the browser paints once (no flicker).      //
   // The follow-up setTimeout defends against DataGrid's own later layout pass.  //
   /////////////////////////////////////////////////////////////////////////////////
   const apply = () =>
   {
      const rootNow = apiRef.current?.rootElementRef?.current as HTMLElement | undefined;
      if (!rootNow)
      {
         return;
      }
      const scrollerNow = rootNow.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null;
      if (!scrollerNow)
      {
         return;
      }
      const headerNow = rootNow.querySelector(`.MuiDataGrid-columnHeader[data-field="${CSS.escape(field)}"]`) as HTMLElement | null;
      if (!headerNow)
      {
         return;
      }
      const scrollerRectNow = scrollerNow.getBoundingClientRect();
      const currentOffsetFromScrollerLeft = headerNow.getBoundingClientRect().left - scrollerRectNow.left;
      const delta = currentOffsetFromScrollerLeft - targetOffsetFromScrollerLeft;
      if (Math.abs(delta) >= 1)
      {
         const newScrollLeft = scrollerNow.scrollLeft + delta;
         scrollerNow.scrollLeft = newScrollLeft;
         ////////////////////////////////////////////////////////////////////////////////
         // The header container tracks body scrollLeft via an async 'rowsScroll'      //
         // event — when the scroll is triggered programmatically (not a UIEvent),     //
         // MUI falls back to a non-flushSync state update so the header lags one      //
         // frame.  Adjust the header inner's translate in the same synchronous        //
         // block by our delta (equivalent to what MUI would compute once the event    //
         // roundtrip resolves; firstColumnToRender is assumed unchanged for the small //
         // deltas produced by edit-time column-width bumps).                          //
         ////////////////////////////////////////////////////////////////////////////////
         const headerInner = rootNow.querySelector(".MuiDataGrid-columnHeadersInner") as HTMLElement | null;
         if (headerInner)
         {
            const match = headerInner.style.transform.match(/translate3d\(([-\d.]+)px,\s*([-\d.]+)px,\s*([-\d.]+)px\)/);
            const curX = match ? parseFloat(match[1]) : 0;
            const curY = match ? parseFloat(match[2]) : 0;
            const curZ = match ? parseFloat(match[3]) : 0;
            headerInner.style.transform = `translate3d(${curX - delta}px, ${curY}px, ${curZ}px)`;
         }
      }
   };
   return () =>
   {
      apply();
      //////////////////////////////////////////////////////////////////////////////////
      // DataGrid may commit a second layout pass (column virtualization recomputing) //
      // after our first correction.  Re-apply on the next frame to stay aligned.     //
      //////////////////////////////////////////////////////////////////////////////////
      requestAnimationFrame(apply);
   };
}


export function captureGridScroll(apiRef: React.MutableRefObject<GridApi>): () => void
{
   const root = apiRef.current?.rootElementRef?.current as HTMLElement | undefined;
   const scroller = root?.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null;
   if (!scroller)
   {
      return () =>
      {
         //////////
         // noop //
         //////////
      };
   }
   const top  = scroller.scrollTop;
   const left = scroller.scrollLeft;
   return () =>
   {
      ///////////////////////////////////////////////////////////////////////////////////
      // DataGridPro resets scrollTop internally when disableVirtualization flips back //
      // to false — and that reset can happen AFTER a single nested-rAF tick, which    //
      // leaves the grid stuck at y=0 even after we wrote our captured top back.       //
      // Prefer the DataGrid scroll API (apiRef.current.scroll) which cooperates with  //
      // DataGrid's internal scroll state; fall back to direct DOM writes otherwise.   //
      // Also retry a few times over a short window in case DataGrid performs more     //
      // than one internal layout pass.                                                //
      ///////////////////////////////////////////////////////////////////////////////////
      const apply = () =>
      {
         const api = apiRef.current;
         if (api?.scroll)
         {
            api.scroll({top, left});
         }
         else
         {
            scroller.scrollTop  = top;
            scroller.scrollLeft = left;
         }
      };
      requestAnimationFrame(() =>
      {
         requestAnimationFrame(() =>
         {
            apply();
            //////////////////////////////////////////////////////////////////////////////
            // Defensive re-apply: DataGridPro sometimes resets scrollTop a tick later. //
            // Two extra late writes cost ~100ms but close the window reliably.         //
            //////////////////////////////////////////////////////////////////////////////
            setTimeout(apply, 50);
            setTimeout(apply, 150);
         });
      });
   };
}
